import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { PackageClientType } from '../models/insurance-package.interface';
import { Package } from '../models/package-configurator/package.model';

/**
 * Validator factory that creates a form-level validator to check if the selected package's client type
 * matches the form's client type.
 *
 * @param getAvailablePackages - Function that returns the current list of available packages
 * @returns ValidatorFn that validates package and client type compatibility
 *
 * @example
 * // Usage in a reactive form
 * this.form = this.fb.group({
 *   clientType: ['B2B', Validators.required],
 *   contracts: this.fb.array([
 *     this.fb.group({
 *       packageId: ['', Validators.required]
 *     })
 *   ])
 * }, {
 *   validators: packageClientTypeValidator(() => this.availablePackages())
 * });
 */
export function packageClientTypeValidator(getAvailablePackages: () => Package[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control;
    const clientType = formGroup.get('clientType')?.value;
    const contracts = formGroup.get('contracts')?.value;

    // Skip validation if required fields are missing
    if (!clientType || !contracts || contracts.length === 0) {
      return null;
    }

    const packageId = contracts[0]?.packageId;
    if (!packageId) {
      return null;
    }

    const availablePackages = getAvailablePackages();
    const selectedPackage = availablePackages.find(pkg => pkg.id === packageId);

    // Skip validation until the package list is loaded
    if (!availablePackages.length) {
      return null;
    }

    if (!selectedPackage) {
      return { packageNotAvailable: true };
    }

    // Allow packages with 'All' type or matching type
    if (selectedPackage.clientType === PackageClientType.All || selectedPackage.clientType === clientType) {
      return null;
    }

    return { packageClientTypeMismatch: true };
  };
}
